/**
 * Centralized display rules for actor usernames (deleted / anonymous / missing).
 * Used by EntityAvatar and related surfaces to avoid duplicated conditionals.
 */

/** Labels shown when identity is obscured or removed. */
export const DISPLAY_IDENTITY_LABELS = {
  anonymous: "[anonymous]",
  deletedUser: "[deleted user]",
} as const;

/**
 * Format a username for display given privacy and deletion signals.
 *
 * Precedence:
 * 1. Explicit anonymous privacy → anonymous label
 * 2. Username contains "deleted" (and not anonymous) → deleted label
 * 3. Non-empty username → as-is
 * 4. Fallback → anonymous label
 *
 * @param username - Raw username from API, if any.
 * @param privacy - `properties.privacy` or equivalent.
 * @returns Safe display string (never throws on undefined username).
 */
export function formatActorDisplayName(
  username: string | undefined | null,
  privacy: string | undefined | null
): string {
  if (privacy === "anonymous") {
    return DISPLAY_IDENTITY_LABELS.anonymous;
  }
  if (typeof username === "string" && username.length > 0) {
    if (username.includes("deleted")) {
      return DISPLAY_IDENTITY_LABELS.deletedUser;
    }
    return username;
  }
  return DISPLAY_IDENTITY_LABELS.anonymous;
}

/**
 * Whether the raw username should be treated as a deleted-account placeholder.
 */
export function isDeletedUsernamePlaceholder(username: string | undefined | null): boolean {
  return typeof username === "string" && username.includes("deleted");
}
